import { styled } from "@linaria/react";
import * as io from "io-ts";
import React from "react";
import createGlobalState from "@ui/lib/createGlobalState";
import useFilteredDatas from "@ui/lib/hooks/useFilteredDatas";
import { useModals } from "@ui/lib/hooks/useModal";
import t from "../../i18n";
import FavouriteCard from "../../components/Projects/FavouriteCard";
import { literalUnion } from "diagnostics-lib/io";
import Button from "@ui/Button";
import { buildOptions, FilterWrapper, useSubStateSetter } from "@ui/Filter";
import FilterSelect from "@ui/Filter/FilterSelect";
import Reset from "@ui/Filter/Reset";
import Modal from "@ui/Modal";
import ScreenRocket from "@ui/Picto/ScreenRocket";
import Section, { SectionContainer } from "@ui/Section";
import SidePanelLayout from "@ui/SidePanel/Layout";
import Switch from "@ui/Switch";
import TableCard from "@ui/Table/Card";
import CreateUpdateProjectsModalForm from "./CreateUpdateProjectsModalForm";
import DeleteProjectModalForm from "./DeleteProjectModalForm";
import type { ProjectModalType } from "./ProjectModalType";
import ProjectTable from "./Table";
import { filterProjects } from "./useFilteredProjects";
import type { Project } from "../../api/useProjects";
import { useProjects } from "../../api/useProjects";
import { useCanUserDo } from "../canUserDo";
import useFavoriteProjectIds from "../../api/useFavoriteProjectIds";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import DuplicateProjectModalForm from "./DuplicateProjectModalForm";
import { AddCircleLine } from "@ui/IconV2/react";
import BlankStateV2 from "@ui/BlankStateV2";
import { useTranslations } from "../../api/useTranslations";
import { TableCardWithoutBorder } from "@ui/Table/_styles/TableCardWithoutBorder";

const FavouriteWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  grid-gap: 15px;
  justify-content: space-between;
  align-items: start;
`;

const ContainerHeaderRight = styled.div`
  display: flex;
  gap: 8px;
`;

const StyledSection = styled(Section)`
  row-gap: 0px;
`;

const FILTER_KEYS = ["site", "type", "sourceId"] as const;
const FilterKey = literalUnion(FILTER_KEYS);
type FilterKey = (typeof FILTER_KEYS)[number];
const FilteredValuesByFilterType = io.record(FilterKey, io.array(io.string));
type FilteredValuesByFilterType = io.TypeOf<typeof FilteredValuesByFilterType>;

const DEFAULT_FILTER_VALUES: FilteredValuesByFilterType = {
  type: [],
  site: [],
  sourceId: [],
};

const useGlobalProjectSearch = createGlobalState({
  storage: "session",
  key: "projectsSearchState",
  type: io.string,
  defaultValue: "",
  scoped: false,
});

const useDisplayDraft = createGlobalState({
  storage: "session",
  key: "projectsTableDisplayNotPublished",
  type: io.boolean,
  scoped: false,
});

const useSelectedProjectIds = createGlobalState({
  storage: "session",
  key: "selectedProjectIds",
  type: io.array(io.string),
  defaultValue: [],
  scoped: false,
});

const useProjectFilters = createGlobalState({
  storage: "session",
  key: "projectFilters",
  type: FilteredValuesByFilterType,
  defaultValue: DEFAULT_FILTER_VALUES,
  scoped: false,
});

function projectComparator(a: Project, b: Project) {
  return a.name.localeCompare(b.name);
}

function projectSearchIndex(project: Project) {
  return project.name;
}

const Projects = () => {
  const translations = useTranslations();
  const [selectedProjectIds, setSelectedProjectIds] = useSelectedProjectIds({});
  const projects = useProjects();
  const [projectFilters, setProjectFilters] = useProjectFilters({});

  const selectedProjects: Project[] = React.useMemo(
    () => projects.filter(p => selectedProjectIds.includes(p.id)),
    [projects, selectedProjectIds],
  );
  const [search, setSearch] = useGlobalProjectSearch({});
  const [selectAction, setSelectAction] = React.useState(selectedProjects.length > 0);
  const [displayDraft, setDisplayDraft] = useDisplayDraft({
    defaultValue: () => projects.every(p => p.draft),
  });

  const canCreateProject = useCanUserDo("PROJECT_MANAGE");
  const canUpdateProjects = useCanUserDo("PROJECT_EDIT");

  const hasDraftProjects = projects.some(p => p.draft);

  const deferredDisplayDraft = React.useDeferredValue(displayDraft);
  const displayDraftInProgress = displayDraft !== deferredDisplayDraft;
  const [project, setProject] = React.useState<Project>();

  const sourceIdSubStateSetter = useSubStateSetter(setProjectFilters, "sourceId");
  const typeIdSubStateSetter = useSubStateSetter(setProjectFilters, "type");
  const siteIdSubStateSetter = useSubStateSetter(setProjectFilters, "site");

  const includedProjects = React.useMemo(
    () => projects.filter(p => !p.draft || displayDraft),
    [displayDraft, projects],
  );

  const filteredProjects = React.useMemo(
    () =>
      filterProjects(includedProjects, projectFilters).filter(
        p => deferredDisplayDraft || !p.draft,
      ),
    [includedProjects, projectFilters, deferredDisplayDraft],
  );

  const [filteredDatas, filteringInProgress] = useFilteredDatas(
    filteredProjects,
    search,
    projectSearchIndex,
  );

  const favoriteProjectIds = useFavoriteProjectIds();

  const favouriteProjects = React.useMemo(
    () => projects.filter(p => favoriteProjectIds.includes(p.id)).sort(projectComparator),
    [projects, favoriteProjectIds],
  );

  const handleCheckboxChange = React.useCallback(() => {
    setDisplayDraft(!displayDraft);
  }, [setDisplayDraft, displayDraft]);

  const { modal, openModal, closeModal } = useModals<ProjectModalType>();

  const openProjectModal = React.useCallback(
    (modalType: ProjectModalType, project: Project) => {
      setProject(project);
      openModal(modalType);
    },
    [openModal],
  );

  const openCreateModal = React.useCallback(() => {
    openModal("create");
  }, [openModal]);

  const openModalForm = React.useCallback(() => {
    openModal("update");
  }, [openModal]);

  const handleRowSelection = React.useCallback(
    (datas: Project[]) => {
      setSelectedProjectIds(datas.map(d => d.id));
    },
    [setSelectedProjectIds],
  );

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    },
    [setSearch],
  );

  const siteIdOptions = React.useMemo(
    () =>
      buildOptions(
        includedProjects
          .filter(p => p.siteId)
          .map(project => ({
            label: project.siteId ?? "",
            value: project.siteId ?? "",
          })),
      ),
    [includedProjects],
  );

  const typeIdOptions = React.useMemo(
    () =>
      buildOptions(
        includedProjects
          .filter(p => p.typeId)
          .map(project => ({
            label: project.typeId ?? "",
            value: project.typeId ?? "",
          })),
      ),
    [includedProjects],
  );

  const sourceIdOptions = React.useMemo(
    () =>
      buildOptions(
        includedProjects.map(project => ({
          label: project.sourceId,
          value: project.sourceId,
        })),
      ),
    [includedProjects],
  );

  return (
    <SidePanelLayout>
      <Modal
        title={t("projects.modal.title.create_project")}
        isOpen={modal === "create"}
        onRequestClose={closeModal}
      >
        <CreateUpdateProjectsModalForm
          onRequestClose={closeModal}
          okButtonName={t("modalForm.button.name.create")}
        />
      </Modal>
      <Modal
        title={
          selectedProjects.length > 1
            ? t("projects.header.actions.update_projects", { count: selectedProjects.length })
            : t("projects.modal.update_project")
        }
        isOpen={modal === "update"}
        onRequestClose={closeModal}
      >
        <CreateUpdateProjectsModalForm
          onRequestClose={closeModal}
          projectIds={
            project && selectedProjectIds.length === 0 ? [project.id] : selectedProjectIds
          }
          okButtonName={t("modalForm.button.name.edit")}
        />
      </Modal>
      {project && (
        <>
          <Modal
            title={t("projects.modal.title.duplicate_project")}
            isOpen={modal === "duplicate"}
            onRequestClose={closeModal}
          >
            <DuplicateProjectModalForm project={project} onRequestClose={closeModal} />
          </Modal>
          <Modal
            title={t("projects.modal.delete.delete_project")}
            isOpen={modal === "delete"}
            onRequestClose={closeModal}
          >
            <DeleteProjectModalForm project={project} onRequestClose={closeModal} />
          </Modal>
        </>
      )}
      {projects.length ? (
        <SectionContainer>
          {favouriteProjects.length ? (
            <Section id="favourites" title={t("projects.paragraphs.favourites")}>
              <FavouriteWrapper data-cy="list-of-favourites">
                {favouriteProjects.map(project => (
                  <FavouriteCard key={project.id} project={project} openModal={openProjectModal} />
                ))}
              </FavouriteWrapper>
            </Section>
          ) : null}
          <StyledSection id="projects" title={t("projects.paragraphs.projects")}>
            <TableCard
              title={null}
              headerLeft={
                <>
                  <SearchInputV2
                    data-cy="search-project"
                    placeholder={t("commons.placeholder.search")}
                    onChange={handleSearchChange}
                    value={search}
                  />
                  <FilterWrapper>
                    <FilterSelect
                      title={t("projects.selectFilter.site")}
                      filtered
                      options={siteIdOptions}
                      values={projectFilters["site"]}
                      setValues={siteIdSubStateSetter}
                    />
                    <FilterSelect
                      title={t("projects.selectFilter.type", translations)}
                      filtered
                      options={typeIdOptions}
                      values={projectFilters["type"]}
                      setValues={typeIdSubStateSetter}
                    />
                    <FilterSelect
                      title={t("projects.selectFilter.data_source")}
                      filtered
                      options={sourceIdOptions}
                      values={projectFilters["sourceId"]}
                      setValues={sourceIdSubStateSetter}
                    />
                    <Reset
                      filterValues={projectFilters}
                      setFilterValues={setProjectFilters}
                      defaultFilterValues={DEFAULT_FILTER_VALUES}
                    />
                  </FilterWrapper>
                  {hasDraftProjects && (
                    <Switch checked={displayDraft} onChange={handleCheckboxChange}>
                      {t("projects.header.display_draft")}
                    </Switch>
                  )}
                </>
              }
              headerRight={
                <ContainerHeaderRight>
                  {canUpdateProjects && selectAction ? (
                    <>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setSelectAction(false);
                          setSelectedProjectIds([]);
                        }}
                      >
                        {t("projects.header.actions.cancel_selection")}
                      </Button>
                      {selectedProjects.length > 0 ? (
                        <Button
                          variant="secondary"
                          onClick={openModalForm}
                          title={t("projects.header.actions.update_projects.info")}
                        >
                          {t("projects.header.actions.update_projects", {
                            count: selectedProjects.length,
                          })}
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          disabled
                          title={t("projects.header.actions.update_projects.info")}
                        >
                          {t("projects.header.actions.update_projects_disabled")}
                        </Button>
                      )}
                    </>
                  ) : (
                    canCreateProject && (
                      <Button variant="secondary" onClick={() => setSelectAction(true)}>
                        {t("projects.header.actions.edit_projects")}
                      </Button>
                    )
                  )}
                  {canCreateProject && (
                    <Button
                      variant="primary"
                      onClick={openCreateModal}
                      data-cy="add-project-button"
                      leftIcon={<AddCircleLine />}
                    >
                      {t("projects.paragraphs.actions.create_project")}
                    </Button>
                  )}
                </ContainerHeaderRight>
              }
              className={TableCardWithoutBorder}
            >
              <ProjectTable
                openModal={openProjectModal}
                loading={filteringInProgress || displayDraftInProgress}
                projects={filteredDatas}
                selectedProjects={selectedProjects}
                handleRowSelection={
                  canUpdateProjects && selectAction ? handleRowSelection : undefined
                }
              />
            </TableCard>
          </StyledSection>
        </SectionContainer>
      ) : (
        <BlankStateV2
          title={t("projects.blank_state.title")}
          subtitle={
            canCreateProject
              ? t("projects.blank_state.create_project")
              : t("projects.blank_state.ask_administrator_for_project_access")
          }
          picto={<ScreenRocket />}
          onClick={canCreateProject ? openCreateModal : undefined}
          buttonLabel={
            canCreateProject ? t("setup.paragraphs.projects.blank_state.button") : undefined
          }
        />
      )}
    </SidePanelLayout>
  );
};

export default React.memo(Projects);
